import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Button } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatNumber, formatNumberWithCurrency } from '@commons/DisplayNumber';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import NavigationBar from '@commons/NavigationBar';

import { canEditExternalClient } from '@selectors/actions/externalClientActions';
import { EXTERNAL_FEATURE_PATHS } from '@commons/utils/featurePaths';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

import { externalClient as externalClientService } from '@services/externalClient';
import { upload as uploadService } from '@services/upload';

import HeaderPicture from '@admin/components/HeaderPicture';

import ClientCommentCard from '../components/ClientCommentCard';
import ClientCompanyCard from '../components/ClientCompanyCard';
import ClientInformationCard from '../components/ClientInformationCard';
import ClientMetricCard from '../components/ClientMetricCard';

import {
  Column,
  Content,
  ContentInner,
  Footer,
  MainGrid,
  MetricsRow,
  PageContainer,
  TopSection,
} from './styledComponents';

const DEFAULT_CLIENT_PICTURE = '/images/inpulse/store-kitchen-photo-placeholder.svg';

/** The revenue is a total, so it sticks to the decimal count of the platform currency. */
const formatRevenueWithTaxes = (value, currency) =>
  formatNumberWithCurrency({
    number: value,
    currency,
    displayCurrencyCode: true,
  });

/**
 * Snapshot of the pristine client used as the dirty-check reference. Must mirror `currentSnapshot`.
 */
const buildSnapshot = (client, associatedStores) => {
  const company = client.company || {};

  return JSON.stringify({
    name: client.name || '',
    storeIds: associatedStores.map(({ id }) => id).sort(),
    externalReference: client.externalReference || '',
    contact: company.contact || '',
    telephone: company.telephone || '',
    contactEmails: company.emails || '',
    address: company.address || '',
    postCode: company.postCode || '',
    city: company.city || '',
    country: company.country || '',
    legalName: company.companyName || '',
    siret: company.siret || '',
    vatNumber: company.vatNumber || '',
    comment: client.comment || '',
    picture: client.picture || null,
  });
};

/**
 * External-client detail page — "Informations" tab (/external/clients/:id/details). Reuses the
 * creation form blocks in read-only mode by default; edition (and the save footer) is unlocked by
 * the `external-client:edit` right. Changing the associated stores prompts a confirmation modal
 * since the server purges the matching catalog products on save. The metrics block shows the
 * current-year orders count and revenue incl. taxes with their YoY evolution (COR-1724).
 */
const ExternalClientDetails = (props) => {
  const {
    match: { path, params },
    stores,
    storeName,
    clientId,
    currency,
    canEdit,
    history,
    pageLoading,
    pageLoaded,
    openConfirmationModal,
    displaySuccessMessage,
    displayErrorMessage,
  } = props;

  const [externalClient, setExternalClient] = useState(null);

  const [metrics, setMetrics] = useState(null);
  const [areMetricsLoading, setAreMetricsLoading] = useState(true);

  const [name, setName] = useState('');
  const [selectedStores, setSelectedStores] = useState([]);
  const [externalReference, setExternalReference] = useState('');
  const [contact, setContact] = useState('');
  const [telephone, setTelephone] = useState('');
  const [contactEmails, setContactEmails] = useState('');
  const [address, setAddress] = useState('');
  const [postCode, setPostCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [legalName, setLegalName] = useState('');
  const [siret, setSiret] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [comment, setComment] = useState('');
  const [picture, setPicture] = useState(null);
  const [selectedPictureFile, setSelectedPictureFile] = useState(null);

  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const associatedStoresLabel = i18next.t('EXTERNAL_CLIENTS.CREATION.ASSOCIATED_STORES', {
    storeName: getClientStoreNameTranslation(storeName, true),
  });

  const isReadOnly = !canEdit;

  /**
   * Hydrate the local form states from a fetched / updated external client and store the resulting
   * snapshot as the new pristine reference used for the dirty check.
   */
  const hydrateFromClient = (client) => {
    const company = client.company || {};
    const associatedStores = stores.filter(({ id }) =>
      (client.stores || []).some(({ id: storeId }) => storeId === id),
    );

    setExternalClient(client);
    setName(client.name || '');
    setSelectedStores(associatedStores);
    setExternalReference(client.externalReference || '');
    setContact(company.contact || '');
    setTelephone(company.telephone || '');
    setContactEmails(company.emails || '');
    setAddress(company.address || '');
    setPostCode(company.postCode || '');
    setCity(company.city || '');
    setCountry(company.country || '');
    setLegalName(company.companyName || '');
    setSiret(company.siret || '');
    setVatNumber(company.vatNumber || '');
    setComment(client.comment || '');
    setPicture(client.picture || null);
    setSelectedPictureFile(null);

    setInitialSnapshot(buildSnapshot(client, associatedStores));
  };

  useEffect(() => {
    if (!params.id) {
      return;
    }

    pageLoading();

    (async () => {
      try {
        const fetchedClient = await externalClientService.getExternalClient(params.id);

        hydrateFromClient(fetchedClient);
      } catch {
        displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.DETAILS.FETCH_ERROR'));
      } finally {
        pageLoaded();
      }
    })();
  }, [params.id]);

  useEffect(() => {
    if (!params.id) {
      setMetrics(null);
      setAreMetricsLoading(false);
      return;
    }

    (async () => {
      setAreMetricsLoading(true);

      try {
        const fetchedMetrics = await externalClientService.getExternalClientMetrics(params.id);

        setMetrics(fetchedMetrics);
      } catch {
        setMetrics(null);
        displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.DETAILS.METRICS_FETCH_ERROR'));
      } finally {
        setAreMetricsLoading(false);
      }
    })();
  }, [params.id]);

  const currentStoreIds = selectedStores.map(({ id }) => id);

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        name,
        storeIds: [...currentStoreIds].sort(),
        externalReference,
        contact,
        telephone,
        contactEmails,
        address,
        postCode,
        city,
        country,
        legalName,
        siret,
        vatNumber,
        comment,
        picture,
      }),
    [
      name,
      currentStoreIds.join(','),
      externalReference,
      contact,
      telephone,
      contactEmails,
      address,
      postCode,
      city,
      country,
      legalName,
      siret,
      vatNumber,
      comment,
      picture,
    ],
  );

  const isDirty =
    (currentSnapshot !== initialSnapshot || !!selectedPictureFile) && !!externalClient;

  const isSaveDisabled = isSaving || !name.trim() || selectedStores.length === 0;

  const displayFooter = canEdit && isDirty;

  const goToList = () => {
    history.push(EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS);
  };

  const handleCancel = () => {
    if (!isDirty) {
      goToList();
      return;
    }

    openConfirmationModal(
      getConfirmationModal({
        title: i18next.t('EXTERNAL_CLIENTS.CREATION.CLOSE_CONFIRMATION_TITLE'),
        icon: '/images/inpulse/warning-white-small.svg',
        content: i18next.t('EXTERNAL_CLIENTS.CREATION.CLOSE_CONFIRMATION_CONTENT'),
        actions: [
          GENERIC_MODAL_CANCEL_BUTTON(),
          {
            key: 1,
            color: 'inpulse-default',
            label: i18next.t('GENERAL.LEAVE'),
            icon: '/images/inpulse/close-white-small.svg',
            handleClick: goToList,
          },
        ],
      }),
    );
  };

  const uploadPicture = async () => {
    const uploadParams = {
      clientId,
      externalClientId: `${params.id}_${moment().format(
        DATE_DISPLAY_FORMATS.UNDERSCORED_MONTH_DAY_HOUR_MINUTE_SECOND,
      )}`,
    };

    return uploadService.uploadFile(selectedPictureFile, uploadParams);
  };

  const buildUpdatePayload = (pictureValue) => ({
    name: name.trim(),
    storeIds: currentStoreIds,
    externalReference: externalReference || null,
    comment: comment || null,
    company: {
      companyName: legalName || null,
      address: address || null,
      postCode: postCode || null,
      city: city || null,
      country: country || null,
      contact: contact || null,
      telephone: telephone || null,
      siret: siret || null,
      vatNumber: vatNumber || null,
      emails: contactEmails || null,
    },
    picture: pictureValue || null,
  });

  const doSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);
    pageLoading();

    try {
      let pictureValue = picture;

      if (selectedPictureFile) {
        const uploadResult = await uploadPicture();

        pictureValue = get(uploadResult, 'data.fileUri', picture);
      }

      const updatedClient = await externalClientService.updateExternalClient(
        params.id,
        buildUpdatePayload(pictureValue),
      );

      hydrateFromClient(updatedClient);

      displaySuccessMessage(i18next.t('EXTERNAL_CLIENTS.DETAILS.SAVE_SUCCESS'));
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.DETAILS.SAVE_ERROR'));
    } finally {
      setIsSaving(false);
      pageLoaded();
    }
  };

  const haveStoresChanged = () => {
    const originalStoreIds = (externalClient.stores || []).map(({ id }) => id);

    if (originalStoreIds.length !== currentStoreIds.length) {
      return true;
    }

    return originalStoreIds.some((id) => !currentStoreIds.includes(id));
  };

  /**
   * Save entry point: when the associated stores changed, confirm first since the server purges the
   * catalog products tied to the removed store perimeter (central / non-central).
   */
  const handleSave = () => {
    if (isSaveDisabled) {
      return;
    }

    if (!haveStoresChanged()) {
      doSave();
      return;
    }

    const storeNamePlural = getClientStoreNameTranslation(storeName, true);

    const storeChangeContent = `${i18next.t('EXTERNAL_CLIENTS.DETAILS.STORE_CHANGE_INTRO', {
      storeName: storeNamePlural,
    })}<ul><li>${i18next.t('EXTERNAL_CLIENTS.DETAILS.STORE_CHANGE_CENTRAL', {
      storeName: storeNamePlural,
    })}</li><li>${i18next.t('EXTERNAL_CLIENTS.DETAILS.STORE_CHANGE_NON_CENTRAL', {
      storeName: storeNamePlural,
    })}</li></ul>`;

    openConfirmationModal(
      getConfirmationModal({
        type: 'warning',
        title: i18next.t('EXTERNAL_CLIENTS.DETAILS.STORE_CHANGE_TITLE'),
        icon: '/images/inpulse/warning-white-small.svg',
        content: storeChangeContent,
        shouldInterpretHtml: true,
        actions: [
          GENERIC_MODAL_CANCEL_BUTTON(),
          {
            key: 1,
            color: 'inpulse-default',
            label: i18next.t('GENERAL.SAVE'),
            icon: '/images/inpulse/save-white-small.svg',
            handleClick: doSave,
          },
        ],
      }),
    );
  };

  const currentYear = moment().year();

  const renderMetrics = () => (
    <MetricsRow>
      <ClientMetricCard
        evolution={metrics?.ordersCount?.evolution ?? null}
        isLoading={areMetricsLoading}
        title={i18next.t('EXTERNAL_CLIENTS.DETAILS.METRIC_ORDERS', { year: currentYear })}
        value={formatNumber(metrics?.ordersCount?.current ?? null)}
      />
      <ClientMetricCard
        evolution={metrics?.revenueWithTaxes?.evolution ?? null}
        isLoading={areMetricsLoading}
        title={i18next.t('EXTERNAL_CLIENTS.DETAILS.METRIC_REVENUE', { year: currentYear })}
        value={formatRevenueWithTaxes(metrics?.revenueWithTaxes?.current, currency)}
      />
    </MetricsRow>
  );

  // While the client is being fetched the page dispatches the global dark loader: rendering the
  // form at that point would flash it empty (placeholders only), so the content waits for the data.
  if (!externalClient) {
    return (
      <>
        <NavigationBar
          externalClient={externalClient}
          externalClientId={params.id}
          path={path}
          storeName={storeName}
        />
        <PageContainer />
      </>
    );
  }

  return (
    <>
      <NavigationBar
        externalClient={externalClient}
        externalClientId={params.id}
        path={path}
        storeName={storeName}
      />
      <PageContainer>
        <Content>
          <ContentInner>
            <TopSection>
              <HeaderPicture
                isDefaultThumbnail={!picture}
                picture={picture || DEFAULT_CLIENT_PICTURE}
                readOnly={isReadOnly}
                setSelectedFile={setSelectedPictureFile}
                title={name}
                onPictureChange={setPicture}
              />
              {renderMetrics()}
            </TopSection>
            <MainGrid>
              <Column>
                <ClientInformationCard
                  associatedStoresLabel={associatedStoresLabel}
                  contact={contact}
                  contactEmails={contactEmails}
                  externalReference={externalReference}
                  isReadOnly={isReadOnly}
                  maxWidth={'100%'}
                  name={name}
                  selectedStores={selectedStores}
                  stores={stores}
                  telephone={telephone}
                  width={'100%'}
                  onChangeContact={setContact}
                  onChangeContactEmails={setContactEmails}
                  onChangeExternalReference={setExternalReference}
                  onChangeName={setName}
                  onChangeStores={setSelectedStores}
                  onChangeTelephone={setTelephone}
                />
                <ClientCompanyCard
                  address={address}
                  city={city}
                  country={country}
                  isReadOnly={isReadOnly}
                  legalName={legalName}
                  maxWidth={'100%'}
                  postCode={postCode}
                  siret={siret}
                  vatNumber={vatNumber}
                  width={'100%'}
                  onChangeAddress={setAddress}
                  onChangeCity={setCity}
                  onChangeCountry={setCountry}
                  onChangeLegalName={setLegalName}
                  onChangePostCode={setPostCode}
                  onChangeSiret={setSiret}
                  onChangeVatNumber={setVatNumber}
                />
              </Column>
              <ClientCommentCard
                comment={comment}
                isReadOnly={isReadOnly}
                maxWidth={'100%'}
                width={'100%'}
                fillHeight
                onChangeComment={setComment}
              />
            </MainGrid>
          </ContentInner>
        </Content>
        {displayFooter && (
          <Footer>
            <Button
              color={'inpulse-outline'}
              handleClick={handleCancel}
              icon={'/images/inpulse/close-black-small.svg'}
              label={i18next.t('GENERAL.CANCEL')}
            />
            <Button
              color={'inpulse-default'}
              handleClick={handleSave}
              icon={'/images/inpulse/save-white-small.svg'}
              isDisabled={isSaveDisabled}
              label={i18next.t('GENERAL.SAVE')}
            />
          </Footer>
        )}
      </PageContainer>
    </>
  );
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS,
  );

  const clientInfo = getClientInfo(state.baseReducer.user);

  return {
    stores: state.baseReducer.activeStores,
    storeName: clientInfo.storeName,
    clientId: clientInfo.clientId,
    currency: state.baseReducer.currency,
    canEdit: state.baseReducer.user.isDeepsight || canEditExternalClient(authorizedActions),
  };
};

const mapDispatchToProps = (dispatch) => ({
  openConfirmationModal: (params) => {
    dispatch(openGenericModal(params));
  },
  displaySuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalClientDetails);
